import * as React from 'react'
import { cn } from '../utils'

export interface TextFieldProps {
  label?: string
  description?: string
  errorMessage?: string
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  className?: string
  inputClassName?: string
  disabled?: boolean
  type?: string
  name?: string
  id?: string
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, description, errorMessage, placeholder, value, onChange, className, inputClassName, disabled, type = 'text', name, id }, ref) => {
    const generatedId = React.useId()
    const inputId = id ?? generatedId
    
    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          name={name}
          type={type}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full px-3 py-2',
            'bg-white border border-gray-300 rounded-lg',
            'text-gray-900 placeholder:text-gray-400',
            'hover:border-gray-400',
            'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
            errorMessage && 'border-red-500 focus:border-red-500 focus:ring-red-500',
            inputClassName
          )}
          aria-invalid={!!errorMessage}
          aria-describedby={description || errorMessage ? `${inputId}-description` : undefined}
        />
        {description && !errorMessage && (
          <p id={`${inputId}-description`} className="text-sm text-gray-500">
            {description}
          </p>
        )}
        {errorMessage && (
          <p id={`${inputId}-description`} className="text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    )
  }
)
TextField.displayName = 'TextField'

export interface TextAreaProps {
  label?: string
  description?: string
  errorMessage?: string
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  className?: string
  inputClassName?: string
  disabled?: boolean
  rows?: number
  name?: string
  id?: string
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, description, errorMessage, placeholder, value, onChange, className, inputClassName, disabled, rows = 3, name, id }, ref) => {
    const generatedId = React.useId()
    const inputId = id ?? generatedId
    
    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          name={name}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className={cn(
            'w-full px-3 py-2',
            'bg-white border border-gray-300 rounded-lg',
            'text-gray-900 placeholder:text-gray-400',
            'hover:border-gray-400',
            'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
            'resize-y min-h-[80px]',
            errorMessage && 'border-red-500 focus:border-red-500 focus:ring-red-500',
            inputClassName
          )}
          aria-invalid={!!errorMessage}
          aria-describedby={description || errorMessage ? `${inputId}-description` : undefined}
        />
        {description && !errorMessage && (
          <p id={`${inputId}-description`} className="text-sm text-gray-500">
            {description}
          </p>
        )}
        {errorMessage && (
          <p id={`${inputId}-description`} className="text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    )
  }
)
TextArea.displayName = 'TextArea'
